import { getParametersConfig, IParametersConfig } from '../config/parameters-config';
import { IAppSettings } from '@msdyn365-commerce/core';

/**
 * Utilisation :
 * Dans les modules la valeur de la configuration se retrouve dans le contexte général sous
 * - this.props?.context?.app?.config?.parametersEnvironment (avec this.props.context.app as Msdyn365.IAppSettings pour typescript)
 * - ou bien via le helper getParametersConfig(this.props?.context?.app)
 */

/**
 * Utilitaire pour gérer les paramètres globaux de l'application
 *
 * UTILISATION DANS LES MODULES :
 *
 * 1. Import des fonctions :
 * ```typescript
 * import { getGlobalParameters, getParameter, getParameters } from '../../common/utilities/global-parameters.utils';
 * ```
 *
 * 2. Récupération du contexte app avec cast TypeScript :
 * ```typescript
 * const appSettings = this.props.context.app as Msdyn365.IAppSettings;
 * ```
 *
 * 3. Récupérer tous les paramètres :
 * ```typescript
 * const allParams = getGlobalParameters(appSettings);
 * const value = allParams["1234567890"];
 * ```
 *
 * 4. Récupérer un paramètre spécifique :
 * ```typescript
 * const param001Value = getParameter("1234567890", appSettings);
 * ```
 *
 * 5. Récupérer plusieurs paramètres spécifiques :
 * ```typescript
 * const configs = getParameters(["1234567890", "9876543210", "5555555555"], appSettings);
 * ```
 *
 * 6. Exemple complet dans un module React (classe) :
 * ```typescript
 * @observer
 * class MyModule extends React.Component<IMyModuleProps, IMyModuleState> {
 *     private getConfiguredValues(): void {
 *         // IMPORTANT: Cast nécessaire pour TypeScript
 *         const appSettings = this.props.context.app as Msdyn365.IAppSettings;
 *
 *         // Récupération des paramètres
 *         const apiUrl = getParameter("1234567890", appSettings);
 *         const configs = getParameters(["9876543210", "5555555555"], appSettings);
 *
 *         console.log('API URL:', apiUrl);
 *         console.log('Config 002:', configs["9876543210"]);
 *     }
 *
 *     public render(): JSX.Element {
 *         return (
 *             <div>
 *                 { Votre composant ici }
 *             </div>
 *         );
 *     }
 * }
 * ```
 *
 * 7. Exemple complet dans un module React (fonction) :
 * ```typescript
 * export const MyModule: React.FC<IMyModuleProps> = ({ config, context }) => {
 *     // IMPORTANT: Cast nécessaire pour TypeScript
 *     const appSettings = context.app as Msdyn365.IAppSettings;
 *
 *     // Récupération des paramètres
 *     const apiUrl = getParameter("1234567890", appSettings);
 *     const configs = getParameters(["9876543210", "5555555555"], appSettings);
 *
 *     return (
 *         <div>
 *             <p>API URL: {apiUrl}</p>
 *             <p>Config 002: {configs["9876543210"]}</p>
 *         </div>
 *     );
 * };
 * ```
 *
 * 8. Vérifier l'existence d'un paramètre :
 * ```typescript
 * const appSettings = this.props.context.app as Msdyn365.IAppSettings;
 * const exists = hasParameter("1234567890", appSettings);
 * ```
 *
 * 9. Accès direct à la configuration d'environnement :
 * ```typescript
 * const appSettings = this.props.context.app as Msdyn365.IAppSettings;
 * const environment = appSettings.config.parametersEnvironment; // 'uat' ou 'prod'
 * ```
 *
 * NOTES IMPORTANTES :
 * - Le cast `as Msdyn365.IAppSettings` est OBLIGATOIRE pour TypeScript
 * - Sans ce cast, vous aurez l'erreur : "Type '{ [x: string]: any; }' is not assignable to parameter of type 'IAppSettings'"
 * - Le cast est sûr car la structure est garantie par le framework Dynamics 365 Commerce
 * - Utilisez toujours ce pattern dans vos modules pour accéder aux paramètres globaux
 */

/**
 * Récupère le nom technique d'un paramètre à partir de son RecordID
 */
export const getTechnicalNameByParameterId = (parameterId: string, appSettings: IAppSettings): string | undefined => {
    const appConfig = appSettings?.config || {};
    // 1. Recherche dans app.settings.json (valeur saisie dans le builder)
    for (const key in appConfig) {
        if (!Object.prototype.hasOwnProperty.call(appConfig, key)) {
            continue;
        }
        const param = appConfig[key];
        // Si la valeur saisie correspond au friendlyName du paramètre, on retourne la clé (le nom technique qui est dans le friendlyName)
        if (param && param === parameterId) {
            return key.toUpperCase();
        }
    }
    // // 2. Fallback sur PROD_CONFIG
    // if (CONFIGS.prod[parameterId]) {
    //     return CONFIGS.prod[parameterId];
    // }
    // // 3. Fallback sur UAT_CONFIG
    // if (CONFIGS.uat[parameterId]) {
    //     return CONFIGS.uat[parameterId];
    // }
    return undefined;
};

/**
 * Récupère tous les paramètres globaux de l'application
 *
 * @param {IAppSettings} appSettings - Configuration de l'application (obtenue via this.props.context.app as Msdyn365.IAppSettings)
 * @returns {IParametersConfig} Tous les paramètres configurés pour l'environnement sélectionné
 */
export const getGlobalParameters = (appSettings: IAppSettings): IParametersConfig => {
    return getParametersConfig(appSettings);
};

/**
 * Récupère un paramètre spécifique par son ID
 *
 * @param {string} parameterId - Le RecordID du paramètre
 * @param {IAppSettings} appSettings - Configuration de l'application (obtenue via this.props.context.app as Msdyn365.IAppSettings)
 * @returns {string | undefined} La valeur du paramètre ou undefined si non trouvé
 */
export const getParameter = (parameterId: string, appSettings: IAppSettings): string | undefined => {
    return getTechnicalNameByParameterId(parameterId, appSettings);
};

/**
 * Récupère plusieurs paramètres spécifiques en une seule fois
 *
 * @param {string[]} parameterIds - Tableau des IDs de paramètres à récupérer
 * @param {IAppSettings} appSettings - Configuration de l'application (obtenue via this.props.context.app as Msdyn365.IAppSettings)
 * @returns {Record<string, string>} Objet contenant les paramètres demandés
 */
export const getParameters = (parameterIds: string[], appSettings: IAppSettings): Record<string, string> => {
    const result: Record<string, string> = {};
    parameterIds.forEach(id => {
        const value = getTechnicalNameByParameterId(id, appSettings);
        if (value) {
            result[id] = value;
        }
    });
    return result;
};

/**
 * Vérifie si un paramètre existe
 *
 * @param {string} parameterId - L'ID du paramètre à vérifier
 * @param {IAppSettings} appSettings - Configuration de l'application (obtenue via this.props.context.app as Msdyn365.IAppSettings)
 * @returns {boolean} True si le paramètre existe
 */
export const hasParameter = (parameterId: string, appSettings: IAppSettings): boolean => {
    return !!getTechnicalNameByParameterId(parameterId, appSettings);
};

/**
 * Récupère tous les IDs de paramètres disponibles
 *
 * @param {IAppSettings} appSettings - Configuration de l'application (obtenue via this.props.context.app as Msdyn365.IAppSettings)
 * @returns {string[]} Tableau des IDs de paramètres
 */
export const getParameterIds = (appSettings: IAppSettings): string[] => {
    const config = getParametersConfig(appSettings);
    return Object.keys(config);
};

/**
 * Fonction de debug pour les helpers de paramètres globaux
 *
 * @param {IAppSettings} appSettings - Configuration de l'application (obtenue via this.props.context.app as Msdyn365.IAppSettings)
 * @returns void
 */
export const testGlobalParametersHelpers = (appSettings: IAppSettings): void => {
    console.log('🧪 === TESTS DES HELPERS GLOBAL PARAMETERS ===');

    // Test 1: Configuration actuelle
    console.log('✅ 1. Configuration actuelle:');
    console.log('   parametersEnvironment:', appSettings.config.parametersEnvironment);

    // Test 2: getGlobalParameters()
    console.log('✅ 2. getGlobalParameters():');
    const allParams = getGlobalParameters(appSettings);
    console.log('   Tous les paramètres:', allParams);
    console.log('   Type:', typeof allParams);
    console.log('   Nombre de paramètres:', Object.keys(allParams).length);

    // Test 3: getParameter() avec paramètres existants
    console.log('✅ 3. getParameter() - paramètres existants:');
    const param1 = getParameter('1234567890', appSettings);
    console.log('   Param 1234567890:', param1);

    // Test 4: getParameter() avec paramètre inexistant
    console.log('✅ 4. getParameter() - paramètre inexistant:');
    const paramInexistant = getParameter('9999999999', appSettings);
    console.log('   Param inexistant (9999999999):', paramInexistant);
    console.log('   Type du résultat:', typeof paramInexistant);

    // Test 5: getParameters() avec plusieurs paramètres
    console.log('✅ 5. getParameters() - plusieurs paramètres:');
    const multiParams = getParameters(['1234567890', '9876543210', '5555555555'], appSettings);
    console.log('   Plusieurs paramètres:', multiParams);
    console.log('   Clés récupérées:', Object.keys(multiParams));

    // Test 6: getParameters() avec mix existant/inexistant
    console.log('✅ 6. getParameters() - mix existant/inexistant:');
    const mixParams = getParameters(['1234567890', '9999999999', '9876543210'], appSettings);
    console.log('   Mix paramètres:', mixParams);
    console.log('   Clés récupérées:', Object.keys(mixParams));

    // Test 7: hasParameter()
    console.log('✅ 7. hasParameter():');
    const exists1 = hasParameter('1234567890', appSettings);
    console.log('   Param 1234567890 existe:', exists1);

    // Test 8: getParameterIds()
    console.log('✅ 8. getParameterIds():');
    const allIds = getParameterIds(appSettings);
    console.log('   Tous les IDs:', allIds);
    console.log("   Nombre total d'IDs:", allIds.length);
    console.log('   Premier ID:', allIds[0]);
    console.log('   Dernier ID:', allIds[allIds.length - 1]);

    console.log('🧪 === FIN DES TESTS ===');
};

/**
 * Constantes pour les strings traduites utilisées comme des booleans
 */
export const BOOLEAN_STRINGS = {
    YES: 'YES',
    YES_FR: 'OUI',
    YES_NL: 'JA',
    NO: 'NO',
    NO_FR: 'NON',
    NO_NL: 'NEE'
} as const;

// Helpers pour les booléens
export const isYes = (value: string | undefined): boolean => {
    if (!value) {
        return false;
    }
    return (
        value.toUpperCase() === BOOLEAN_STRINGS.YES ||
        value.toUpperCase() === BOOLEAN_STRINGS.YES_FR ||
        value.toUpperCase() === BOOLEAN_STRINGS.YES_NL
    );
};

export const isNo = (value: string | undefined): boolean => {
    if (!value) {
        return false;
    }
    return (
        value.toUpperCase() === BOOLEAN_STRINGS.NO ||
        value.toUpperCase() === BOOLEAN_STRINGS.NO_FR ||
        value.toUpperCase() === BOOLEAN_STRINGS.NO_NL
    );
};
